import { Button } from "@/components/ui/button";
import { Pill, PlayCircle, Award, ClipboardCheck, RotateCcw, ArrowRight } from "lucide-react";
import { FurtherReading } from "@/components/FurtherReading";
import { SaveSession } from "@/components/SaveSession";

export const WardRoundsIntro = ({ caseCount, onStart }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-8 shadow-sm fade-up text-center max-w-2xl mx-auto">
    <div className="w-14 h-14 rounded-xl bg-[#0047AB]/10 flex items-center justify-center mx-auto">
      <Pill className="w-7 h-7 text-[#0047AB]" />
    </div>
    <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-4">
      Ward Rounds &amp; Medications
    </h1>
    <p className="text-base text-slate-600 mt-3 leading-relaxed">
      Run a hospitalised patient's medication round across five staged beats —{" "}
      <span className="font-semibold text-slate-800">read the treatment sheet</span>, run the{" "}
      <span className="font-semibold text-slate-800">medication rights</span> checks, calculate the
      correct dose volume, give it by the right route, then record and sign off the round. Catch the
      duplicate orders, unsigned doses and dose-query red flags. Graded against a Cert IV
      medication-administration rubric.
    </p>
    <div className="flex flex-wrap items-center justify-center gap-2 mt-5">
      {["Treatment sheet", "5 rights", "Dose maths", "Route & technique", "Sign-off & S8 register"].map((t) => (
        <span key={t} className="text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded bg-slate-100 text-slate-600">
          {t}
        </span>
      ))}
    </div>
    <div className="mt-7">
      <Button onClick={onStart} data-testid="start-drill-btn" className="bg-[#0047AB] hover:bg-[#0a3f8f] text-white text-base px-6 py-5">
        <PlayCircle className="w-5 h-5 mr-2" /> Start a ward round · {caseCount} cases
      </Button>
    </div>
    <FurtherReading topic="wardrounds" className="mt-8 text-left" />
  </div>
);

const statusStyle = {
  due: { label: "DUE NOW", cls: "bg-amber-100 text-amber-800 border-amber-300", dot: "#f59e0b" },
  signed: { label: "GIVEN", cls: "bg-emerald-100 text-emerald-700 border-emerald-300", dot: "#10b981" },
  running: { label: "RUNNING", cls: "bg-sky-100 text-sky-700 border-sky-300", dot: "#0ea5e9" },
  later: { label: "NOT DUE", cls: "bg-slate-100 text-slate-500 border-slate-300", dot: "#cbd5e1" },
  hold: { label: "HOLD", cls: "bg-slate-100 text-slate-500 border-slate-300", dot: "#94a3b8" },
  query: { label: "QUERY", cls: "bg-red-100 text-red-700 border-red-300", dot: "#ef4444" },
};

const toMin = (t) => {
  const [h, m] = String(t || "12:00").split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
};

// 24-hour medication clock: each charted med plotted at its scheduled time,
// colour-coded by status, with a live "NOW" marker so what's due reads at a glance.
export const MedTimeline = ({ sheet, nowTime }) => {
  const now = toMin(nowTime);
  const pct = (min) => (min / 1440) * 100;
  return (
    <div className="rounded-md border border-slate-300 bg-white p-3" data-testid="med-timeline">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">24-hour medication clock</span>
        <span className="text-[11px] font-mono-clinical font-bold text-[#0047AB]" data-testid="timeline-now">NOW {nowTime}</span>
      </div>
      <div className="relative h-12 mt-2">
        <div className="absolute left-0 right-0 top-6 h-0.5 bg-slate-200" />
        {[0, 6, 12, 18, 24].map((h) => (
          <div key={h} className="absolute top-6" style={{ left: `${pct(h * 60)}%` }}>
            <div className="w-px h-2 bg-slate-300" />
            <div className="text-[9px] text-slate-400 mt-0.5 -translate-x-1/2">{String(h).padStart(2, "0")}:00</div>
          </div>
        ))}
        <div className="absolute top-1 bottom-3 w-0.5 bg-[#0047AB]" style={{ left: `${pct(now)}%` }} data-testid="timeline-now-marker" />
        {sheet.rows.map((r, i) => {
          const s = statusStyle[r.status] || statusStyle.later;
          return (
            <div key={`${r.time}-${r.drug}`} className="absolute top-6 -translate-x-1/2 -translate-y-1/2" style={{ left: `${pct(toMin(r.time))}%` }} data-testid={`timeline-dot-${i}`} title={`${r.time} · ${r.drug}`}>
              <span className="block w-3 h-3 rounded-full border-2 border-white shadow" style={{ background: s.dot }} />
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Rendered hospital treatment sheet used in beat 1 (and on later beats for reference).
export const TreatmentSheet = ({ sheet }) => (
  <div className="rounded-md border border-slate-300 bg-white overflow-hidden" data-testid="treatment-sheet">
    <div className="bg-slate-800 text-white px-3.5 py-2 text-xs font-semibold font-mono-clinical tracking-wide">
      {sheet.title}
    </div>
    <div className="divide-y divide-slate-100">
      <div className="hidden sm:grid grid-cols-12 gap-2 px-3.5 py-1.5 bg-slate-50 text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
        <span className="col-span-2">Time</span>
        <span className="col-span-5">Order</span>
        <span className="col-span-2">Freq</span>
        <span className="col-span-3 text-right">Status</span>
      </div>
      {sheet.rows.map((r, i) => {
        const s = statusStyle[r.status] || statusStyle.later;
        return (
          <div key={`${r.time}-${r.drug}`} className="grid grid-cols-12 gap-2 px-3.5 py-2 items-center" data-testid={`sheet-row-${i}`}>
            <span className="col-span-3 sm:col-span-2 font-mono-clinical text-sm font-bold text-slate-900">{r.time}</span>
            <span className="col-span-9 sm:col-span-5 text-sm text-slate-800 leading-snug">
              {r.drug}
              {r.note && <span className="block text-[11px] text-slate-400 font-mono-clinical">{r.note}</span>}
            </span>
            <span className="col-span-6 sm:col-span-2 text-[11px] text-slate-500 font-mono-clinical">{r.freq}</span>
            <span className="col-span-6 sm:col-span-3 sm:text-right">
              <span className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded border ${s.cls}`}>{s.label}</span>
            </span>
          </div>
        );
      })}
    </div>
  </div>
);

const bandStyle = (percent) =>
  percent >= 80
    ? { label: "Competent", text: "text-emerald-600", ring: "border-emerald-300 bg-emerald-50" }
    : percent >= 60
    ? { label: "Developing", text: "text-amber-600", ring: "border-amber-300 bg-amber-50" }
    : { label: "Not yet competent", text: "text-red-600", ring: "border-red-300 bg-red-50" };

export const WardRoundsDebrief = ({ result, onNext, onRestart }) => {
  const { percent, breakdown, caseTitle } = result;
  const s = bandStyle(percent);

  return (
    <div className="space-y-5 fade-up">
      <div className="bg-white border border-emerald-200 rounded-xl p-6 shadow-lg" data-testid="wardrounds-debrief">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-emerald-700 font-semibold mb-5">
          <ClipboardCheck className="w-4 h-4" />
          <span>Medication round debrief · {caseTitle}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="flex items-center gap-5">
            <div className={`flex flex-col items-center justify-center w-24 h-24 rounded-2xl border-4 ${s.ring}`} data-testid="debrief-grade">
              <Award className={`w-5 h-5 ${s.text}`} />
              <span className={`text-4xl font-bold leading-none ${s.text}`} data-testid="debrief-percent">{percent}</span>
              <span className="text-[10px] text-slate-400 font-semibold">/100</span>
            </div>
            <div>
              <div className={`text-xl font-bold ${s.text}`} data-testid="debrief-band">{s.label}</div>
              <div className="text-xs text-slate-500 mt-1">Cert IV medication competency</div>
            </div>
          </div>

          <div className="lg:col-span-2" data-testid="debrief-breakdown">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Stage scores</div>
            <div className="space-y-2">
              {breakdown.map((b) => {
                const pct = Math.round((b.earned / b.max) * 100);
                return (
                  <div key={b.key} data-testid={`debrief-stage-${b.key}`}>
                    <div className="flex items-center justify-between text-xs mb-0.5">
                      <span className="text-slate-700 font-medium">{b.label}</span>
                      <span className="font-mono-clinical font-semibold text-slate-800">{b.earned}/{b.max}</span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${pct}%`, backgroundColor: pct >= 80 ? "#10b981" : pct >= 50 ? "#f59e0b" : "#ef4444" }}
                      />
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{b.detail}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-3">
          <Button onClick={onNext} data-testid="next-case-btn" className="bg-[#0047AB] hover:bg-[#0a3f8f] text-white">
            Next patient <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
          <Button onClick={onRestart} variant="outline" data-testid="restart-btn" className="text-slate-600">
            <RotateCcw className="w-4 h-4 mr-1.5" /> Back to start
          </Button>
        </div>
      </div>

      <SaveSession
        simulatorId="wardrounds"
        simulatorName="Ward Rounds & Medications"
        score={percent}
        grade={s.label}
        summary={`${caseTitle} · ${s.label}`}
      />

      <FurtherReading topic="wardrounds" />
    </div>
  );
};
